import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Wrench, Lock, Eye, EyeOff, Loader2, CheckCircle2, 
  Building2, Mail, Phone, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/logos/sterilemate-white.png?v=4";

export default function ContractorSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [contractor, setContractor] = useState(null);
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Verify token on load
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await fetch(`${API}/contractor/setup/${token}`);
        
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Invalid or expired setup link");
        }
        
        const data = await res.json();
        setContractor(data.contractor);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (!acceptTerms) {
      toast.error("Please accept the Terms of Service");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contractor/setup/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password,
          accept_terms: acceptTerms
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Setup failed");
      }
      
      const data = await res.json();
      
      // Store token and redirect to portal (like Hospital flow)
      if (data.token) {
        localStorage.setItem("contractor_token", data.token);
        if (data.contractor) {
          localStorage.setItem("contractor_user", JSON.stringify(data.contractor));
        }
        toast.success("Account setup complete! Welcome to AskIsaac.");
        navigate("/contractor/dashboard");
      } else {
        toast.success("Account setup complete! You can now log in.");
        navigate("/contractor/login");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Setup Link Invalid</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/contractor/login")} variant="outline" className="border-slate-600 text-slate-300">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Sterile Mate" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Welcome to AskIsaac</h1>
          <p className="text-slate-400">Contractor Portal Setup</p>
        </div>

        {/* Contractor Info Card */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-semibold">{contractor?.company_name}</p>
              <p className="text-slate-400 text-sm">{contractor?.email}</p>
            </div>
          </div>
          <p className="text-emerald-400 text-sm">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Your account has been pre-approved. Set your password to get started.
          </p>
        </div>

        {/* Setup Form */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-slate-300">Create Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="pl-10 pr-10 bg-slate-900 border-slate-600 text-white"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  required
                />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                className="mt-1 border-slate-600 data-[state=checked]:bg-emerald-500"
              />
              <label htmlFor="terms" className="text-sm text-slate-400 cursor-pointer">
                I agree to the <a href="/terms" className="text-emerald-400 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</a>
              </label>
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Setting up...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Complete Setup</>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Already have an account?{" "}
          <a href="/contractor/login" className="text-emerald-400 hover:underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
